/*
 * The Procurement screen's state.
 *
 * `comparisonOf` is the derivation the frame's RFQ panel is for. An RFQ with three bids is not a
 * row of numbers — it is a decision with a spread, a cheapest option, and a budget it either fits
 * or does not. Computing that once here means the table and the drawer cannot disagree about which
 * bid is lowest or whether the award went over budget.
 *
 * The server's two rules are mirrored rather than re-implemented: an RFQ cannot be awarded without
 * a winning bid, and the winning bid will not delete while it is the winner. The screen keeps a
 * person from trying either, and the server refuses anyway if they get past it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, opsApi } from '../../../api';
import type { OpsBidDto, OpsRfqDto, OpsRfqInput, OpsRfqStatus } from '../../../api';

export interface RfqComparison {
  rfq: OpsRfqDto;
  cheapest: OpsBidDto | null;
  dearest: OpsBidDto | null;
  awarded: OpsBidDto | null;
  /** What the spread between the best and worst bid is worth — the value of comparing at all. */
  spread: number;
  /** Positive means the awarded (or cheapest) bid is over the budget target. */
  overBudget: number | null;
}

export function comparisonOf(rfq: OpsRfqDto): RfqComparison {
  const sorted = [...rfq.bids].sort((a, b) => a.totalBidAmount - b.totalBidAmount);
  const cheapest = sorted[0] ?? null;
  const dearest = sorted[sorted.length - 1] ?? null;
  const awarded = rfq.bids.find((b) => b.id === rfq.awardedBidId) ?? null;
  const against = awarded ?? cheapest;
  return {
    rfq,
    cheapest,
    dearest,
    awarded,
    spread: cheapest && dearest ? dearest.totalBidAmount - cheapest.totalBidAmount : 0,
    overBudget: rfq.budgetTarget !== null && against
      ? against.totalBidAmount - rfq.budgetTarget
      : null,
  };
}

export interface OpsProcurementState {
  rfqs: OpsRfqDto[];
  comparisons: Map<string, RfqComparison>;
  loading: boolean;
  loadError: string;
  error: string;
  busyId: string | null;
  create: (input: OpsRfqInput) => Promise<void>;
  update: (rfq: OpsRfqDto, patch: Partial<OpsRfqInput>) => Promise<void>;
  award: (rfq: OpsRfqDto, bidId: string) => Promise<void>;
  addBid: (rfq: OpsRfqDto, vendorName: string, totalBidAmount: number, notes: string) => Promise<void>;
  removeBid: (rfq: OpsRfqDto, bid: OpsBidDto) => Promise<void>;
  remove: (rfq: OpsRfqDto) => Promise<void>;
  reload: () => Promise<void>;
}

export function useOpsProcurement(token: string, eventSlug?: string): OpsProcurementState {
  const [rfqs, setRfqs] = useState<OpsRfqDto[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRfqs(await opsApi.listRfqs(token, eventSlug));
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดข้อมูลจัดซื้อไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const run = useCallback(async (id: string | null, fn: () => Promise<unknown>, fallback: string) => {
    setBusyId(id);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusyId(null);
    }
  }, [load]);

  const list = rfqs ?? [];

  return {
    rfqs: list,
    comparisons: useMemo(() => new Map(list.map((r) => [r.id, comparisonOf(r)])), [list]),
    loading: rfqs === null && !loadError,
    loadError, error, busyId,
    create: (input) => run(null, () => opsApi.createRfq(token, input, eventSlug), 'สร้าง RFQ ไม่สำเร็จ'),
    update: (rfq, patch) =>
      run(rfq.id, () => opsApi.updateRfq(token, rfq.id, { ...patch, version: rfq.version }, eventSlug), 'บันทึกไม่สำเร็จ'),
    award: (rfq, bidId) =>
      run(rfq.id, () => opsApi.updateRfq(token, rfq.id, { status: 'awarded' as OpsRfqStatus, awardedBidId: bidId, version: rfq.version }, eventSlug), 'ตัดสินผู้ชนะไม่สำเร็จ'),
    addBid: (rfq, vendorName, totalBidAmount, notes) =>
      run(rfq.id, () => opsApi.addBid(token, rfq.id, { vendorName, totalBidAmount, notes }, eventSlug), 'เพิ่มใบเสนอราคาไม่สำเร็จ'),
    removeBid: (rfq, bid) =>
      run(rfq.id, () => opsApi.deleteBid(token, rfq.id, bid.id, bid.version, eventSlug), 'ลบใบเสนอราคาไม่สำเร็จ'),
    remove: (rfq) => run(rfq.id, () => opsApi.deleteRfq(token, rfq.id, rfq.version, eventSlug), 'ลบ RFQ ไม่สำเร็จ'),
    reload: load,
  };
}
